import { Injectable } from '@angular/core';
import { AuthService } from '@telespot/web-core';
import { RolesStore } from '@telespot/authentication/ui';
import { AffiliatedUsersStore, UserCreatedStatus } from '../../../stores/user/affiliated-user.store';
import { UserMembershipsEffects } from '../../../stores/user/user-memberships.effects';
import { WorkspacesEffects } from '../../../stores/workspaces.effects';
import { BaseUserFacade } from '../base-user.facade';

/**
 * Facade for the UserCreator component.
 * Coordinates AffiliatedUsersStore, RolesStore, and WorkspacesEffects for user creation flow.
 */
@Injectable()
export class UserCreatorFacade extends BaseUserFacade {
  constructor(
    affiliatedUserStore: AffiliatedUsersStore,
    userMembershipsEffects: UserMembershipsEffects,
    rolesStore: RolesStore,
    workspacesEffects: WorkspacesEffects,
    authService: AuthService
  ) {
    super(affiliatedUserStore, userMembershipsEffects, rolesStore, workspacesEffects, authService);
  }

  // Creator-specific selectors
  public get registerWithoutEmail$() {
    return this.affiliatedUserStore.registerWithoutEmail$;
  }

  public get addExistingUser$() {
    return this.affiliatedUserStore.addExistingUser$;
  }

  public get userForm$() {
    return this.affiliatedUserStore.userForm$;
  }

  public get userFormIsValid$() {
    return this.affiliatedUserStore.userFormIsValid$;
  }

  public get organizationLimitReached$() {
    return this.affiliatedUserStore.organizationLimitReached$;
  }

  public readonly userCreationStatus$ = this.select(
    this.affiliatedUserStore.userCreated$,
    this.affiliatedUserStore.rolesUpdated$,
    this.affiliatedUserStore.addedToOrganization$,
    this.affiliatedUserStore.existingUserAssignedToOrganization$,
    this.affiliatedUserStore.error$,
    this.userMembershipsEffects.addedToWorkspaces$,
    this.userMembershipsEffects.removedFromWorkspaces$,
    this.userMembershipsEffects.loadError$,
    (
      userCreated,
      rolesUpdated,
      addedToOrganization,
      existingUserAssignedToOrganization,
      affiliatedError,
      addedToWorkspaces,
      removedFromWorkspaces,
      membershipError
    ) => {
      if (
        userCreated &&
        rolesUpdated &&
        addedToWorkspaces &&
        removedFromWorkspaces &&
        addedToOrganization &&
        !affiliatedError &&
        !membershipError
      ) {
        return existingUserAssignedToOrganization
          ? UserCreatedStatus.ASSIGNED_TO_ORGANIZATION
          : UserCreatedStatus.CREATED;
      }
      return UserCreatedStatus.NOT_CREATED;
    }
  );

  // Creator-specific updaters
  public readonly toggleRegisterWithoutEmail = (value: boolean) => {
    this.affiliatedUserStore.toggleRegisterWithoutEmail(value);
  };

  public readonly toggleAddExistingUser = (value: boolean) => {
    this.affiliatedUserStore.toggleAddExistingUser(value);
  };

  public readonly setFormUsername = (username: string) => {
    this.affiliatedUserStore.setFormUsername(username);
  };

  public readonly setFormPassword = (password: string) => {
    this.affiliatedUserStore.setFormPassword(password);
  };

  // Creator-specific effects
  public readonly setFormEmail = (email: string) => {
    this.affiliatedUserStore.setFormEmail(email);
  };

  public readonly createAndAffiliateUser = () => {
    this.affiliatedUserStore.createAndAffiliateUser();
  };

  /**
   * Creator-specific initialization: calls initializeForm on the affiliated user store.
   */
  protected initializeSpecific(params: { organizationId: string; maxUsers: number }): void {
    this.affiliatedUserStore.initializeForm(params);
  }
}
